import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { expedientesAPI, pendientesListaAPI, usuariosAPI } from '../services/api';

/** Datos viejos en localStorage (antes de API); se importan una vez si el servidor está vacío */
const STORAGE_KEY_LEGACY = 'expedientes_pendientes_v1';

const CAT_COMUN = 'COMUN';
const CAT_GENERAL = 'GENERAL';

/** Listado en pantalla: dos bloques fijos sin pestañas */
const SECCIONES_PENDIENTES = [
  { cat: CAT_COMUN, titulo: 'Diario' },
  { cat: CAT_GENERAL, titulo: 'Generales' }
];

const categoriaOf = (item) => (item && item.categoria) || CAT_COMUN;

const decodeLabel = (rawLabel = '') => {
  try {
    return decodeURIComponent(rawLabel);
  } catch {
    return rawLabel;
  }
};

/** Corta etiquetas en texto + tags (soporta #tipo:id|etiqueta con espacios y formato antiguo #tipo:id:encoded) */
const extractFragments = (text) => {
  const out = [];
  let i = 0;

  while (i < text.length) {
    const sub = text.slice(i);
    const rel = sub.search(/#(exp|usr|obra):/i);
    if (rel === -1) {
      out.push({ type: 'text', value: sub });
      break;
    }
    if (rel > 0) {
      out.push({ type: 'text', value: sub.slice(0, rel) });
    }
    i += rel;
    const tokenStr = text.slice(i);

    const newHead = tokenStr.match(/^#(exp|usr|obra):(\d+)\|/i);
    if (newHead) {
      const headLen = newHead[0].length;
      const rest = tokenStr.slice(headLen);
      const nextIdx = rest.search(/\s+#(exp|usr|obra):\d+\|/i);
      const label = (nextIdx === -1 ? rest : rest.slice(0, nextIdx)).replace(/\s+$/, '');
      const consumed = headLen + (nextIdx === -1 ? rest.length : nextIdx);
      out.push({
        type: 'tag',
        kind: newHead[1].toLowerCase(),
        id: newHead[2],
        label,
        raw: tokenStr.slice(0, consumed)
      });
      i += consumed;
      continue;
    }

    const oldMatch = tokenStr.match(/^#(exp|usr|obra):([^:\s]+):([^\s]+)/i);
    if (oldMatch) {
      out.push({
        type: 'tag',
        kind: oldMatch[1].toLowerCase(),
        id: oldMatch[2],
        label: decodeLabel(oldMatch[3]),
        raw: oldMatch[0]
      });
      i += oldMatch[0].length;
      continue;
    }

    out.push({ type: 'text', value: tokenStr[0] });
    i += 1;
  }

  return out;
};

const formatTagForInput = (tag) => {
  const safeLabel = String(tag.label || '').replace(/\|/g, '·');
  return `#${tag.kind}:${tag.id}|${safeLabel}`;
};

const renderFragments = (text, { onExpedienteClick, expClickable }) => {
  const fragments = extractFragments(text);
  return fragments.map((frag, idx) => {
    if (frag.type === 'text') {
      return <span key={`t-${idx}`}>{frag.value}</span>;
    }

    const className = `pendientes-tag pendientes-tag-${frag.kind}`;

    if (frag.kind === 'exp' && expClickable && onExpedienteClick) {
      return (
        <button
          key={`tag-${idx}`}
          type="button"
          className={`${className} pendientes-tag-clickable`}
          onClick={() => onExpedienteClick(frag.id)}
          title="Ir al expediente"
        >
          #{frag.label}
        </button>
      );
    }

    return (
      <span key={`tag-${idx}`} className={className}>
        #{frag.label}
      </span>
    );
  });
};

const renderTextWithTags = (text, onExpedienteClick) =>
  renderFragments(text, { onExpedienteClick, expClickable: true });

const PendientesModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const composeRef = useRef(null);
  const mirrorRef = useRef(null);
  const [pendientes, setPendientes] = useState([]);
  const [draft, setDraft] = useState('');
  const [tokenInfo, setTokenInfo] = useState(null);
  const [sugerencias, setSugerencias] = useState([]);
  const [loadingSugerencias, setLoadingSugerencias] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [loadingLista, setLoadingLista] = useState(false);
  const [errorLista, setErrorLista] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  /** Conmutador del formulario: solo la categoría al guardar */
  const [destinoGuardado, setDestinoGuardado] = useState(CAT_COMUN);
  /** Lista inferior: solo una categoría visible con scroll; distinto visual del toggle de guardado */
  const [listaVista, setListaVista] = useState(CAT_COMUN);
  /** Aumentar alto útil cuando hay muchas filas */
  const [listaAmpliada, setListaAmpliada] = useState(false);
  /** “Hechas” colapsadas por categoría (Diario / Generales) */
  const [mostrarHechasPorCat, setMostrarHechasPorCat] = useState(() => ({
    [CAT_COMUN]: false,
    [CAT_GENERAL]: false
  }));
  /** Fila que muestra animación verde antes de salir del listado (completar o eliminar) */
  const [salidaAnimId, setSalidaAnimId] = useState(null);
  /** Checkbox “marcado” de forma visual durante el festejo al pasar una tarea activa a hechas */
  const [completandoVisualId, setCompletandoVisualId] = useState(null);

  const msAnimSalida = () =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 90
      : 400;

  const cargarLista = useCallback(async () => {
    setLoadingLista(true);
    setErrorLista(null);
    try {
      const res = await pendientesListaAPI.listar();
      let lista = Array.isArray(res.data) ? res.data : [];

      if (lista.length === 0) {
        const raw = localStorage.getItem(STORAGE_KEY_LEGACY);
        if (raw) {
          try {
            const legacy = JSON.parse(raw);
            if (Array.isArray(legacy) && legacy.length > 0) {
              for (const p of legacy) {
                if (!p || typeof p.texto !== 'string' || !p.texto.trim()) continue;
                await pendientesListaAPI.crear({
                  texto: p.texto.trim(),
                  completado: !!p.completado,
                  categoria: CAT_COMUN
                });
              }
              localStorage.removeItem(STORAGE_KEY_LEGACY);
              const res2 = await pendientesListaAPI.listar();
              lista = Array.isArray(res2.data) ? res2.data : [];
            }
          } catch (e) {
            console.error('Migración pendientes legacy:', e);
          }
        }
      }

      setPendientes(lista);
    } catch (error) {
      console.error('Error cargando pendientes:', error);
      setErrorLista(
        error?.response?.data?.error ||
          error?.message ||
          'No se pudo cargar la lista. ¿Aplicaste la migración en el servidor?'
      );
      setPendientes([]);
    } finally {
      setLoadingLista(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setErrorLista(null);
      setDraft('');
      setTokenInfo(null);
      setSugerencias([]);
      setDestinoGuardado(CAT_COMUN);
      setListaVista(CAT_COMUN);
      setListaAmpliada(false);
      setMostrarHechasPorCat({ [CAT_COMUN]: false, [CAT_GENERAL]: false });
      setSalidaAnimId(null);
      setCompletandoVisualId(null);
      return;
    }
    setDraft('');
    setTokenInfo(null);
    setSugerencias([]);
    setDestinoGuardado(CAT_COMUN);
    setListaVista(CAT_COMUN);
    setListaAmpliada(false);
    setMostrarHechasPorCat({ [CAT_COMUN]: false, [CAT_GENERAL]: false });
    cargarLista();
  }, [isOpen, cargarLista]);

  /** Foco en el texto al abrir (autoFocus + refuerzo por si el overlay retrasa el paint) */
  useEffect(() => {
    if (!isOpen) return;
    const id = window.setTimeout(() => {
      composeRef.current?.focus({ preventScroll: true });
    }, 0);
    const id2 = window.setTimeout(() => {
      composeRef.current?.focus({ preventScroll: true });
    }, 120);
    return () => {
      window.clearTimeout(id);
      window.clearTimeout(id2);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const cargarClientes = async () => {
      try {
        const response = await expedientesAPI.getClientes();
        setClientes(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error cargando obras (clientes):', error);
      }
    };

    cargarClientes();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !tokenInfo) {
      setSugerencias([]);
      return;
    }

    const query = tokenInfo.query.trim();
    if (query.length < 1) {
      setSugerencias([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setLoadingSugerencias(true);

        const [expRes, usrRes] = await Promise.all([
          expedientesAPI.getExpedientes({
            page: 1,
            pageSize: 6,
            search: query,
            sort: 'numero',
            order: 'desc'
          }),
          usuariosAPI.buscarUsuarios(query)
        ]);

        const listaExp = expRes.data?.expedientes || [];

        const expedientes = listaExp.slice(0, 4).map((item) => ({
          kind: 'exp',
          id: String(item.id),
          label: item.numero,
          sublabel: item.cliente?.nombre ? `Cliente: ${item.cliente.nombre}` : 'Expediente'
        }));

        const usuarios = (Array.isArray(usrRes.data) ? usrRes.data : []).slice(0, 4).map((item) => ({
          kind: 'usr',
          id: String(item.id),
          label: item.nombre,
          sublabel: item.rol || 'Usuario'
        }));

        const obraKeys = new Set();
        const obrasDesdeExp = [];
        for (const item of listaExp) {
          const cid = item.clienteId ?? item.cliente?.id;
          if (!cid || !item.cliente?.nombre) continue;
          const label = `${item.cliente.nombre} - ${item.numero}`;
          const key = `${cid}-${item.numero}`;
          if (obraKeys.has(key)) continue;
          obraKeys.add(key);
          obrasDesdeExp.push({
            kind: 'obra',
            id: String(cid),
            label,
            sublabel: item.numero
          });
        }

        const obrasSoloCliente = clientes
          .filter((cliente) => cliente.nombre?.toLowerCase().includes(query.toLowerCase()))
          .filter((cliente) => !listaExp.some((e) => (e.clienteId ?? e.cliente?.id) === cliente.id))
          .slice(0, 3)
          .map((cliente) => ({
            kind: 'obra',
            id: String(cliente.id),
            label: cliente.nombre,
            sublabel: 'Cliente (sin expediente en esta búsqueda)'
          }));

        const obras = [...obrasDesdeExp.slice(0, 6), ...obrasSoloCliente];

        setSugerencias([...expedientes, ...obras, ...usuarios]);
      } catch (error) {
        console.error('Error cargando sugerencias de tags:', error);
        setSugerencias([]);
      } finally {
        setLoadingSugerencias(false);
      }
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [tokenInfo, clientes, isOpen]);

  const pendientesOrdenados = useMemo(
    () =>
      [...pendientes].sort((a, b) => {
        if (Boolean(a.completado) !== Boolean(b.completado)) {
          return Number(a.completado) - Number(b.completado);
        }
        const ca = categoriaOf(a);
        const cb = categoriaOf(b);
        if (ca !== cb) return ca.localeCompare(cb);
        if ((a.orden ?? 0) !== (b.orden ?? 0)) {
          return (a.orden ?? 0) - (b.orden ?? 0);
        }
        return (a.id ?? 0) - (b.id ?? 0);
      }),
    [pendientes]
  );

  const conteosPorCategoria = useMemo(() => {
    const base = { [CAT_COMUN]: 0, [CAT_GENERAL]: 0 };
    for (const p of pendientes) {
      base[categoriaOf(p)] += 1;
    }
    return base;
  }, [pendientes]);

  const detectarToken = (value, caretPosition) => {
    const beforeCaret = value.slice(0, caretPosition);
    const match = beforeCaret.match(/(^|\s)#([^\s#:]*)$/);
    if (!match) return null;

    return {
      query: match[2],
      start: caretPosition - match[2].length - 1,
      end: caretPosition
    };
  };

  const syncMirrorScroll = () => {
    const ta = composeRef.current;
    const mirror = mirrorRef.current;
    if (ta && mirror) {
      mirror.scrollTop = ta.scrollTop;
      mirror.scrollLeft = ta.scrollLeft;
    }
  };

  const handleDraftChange = (event) => {
    const value = event.target.value;
    const caret = event.target.selectionStart ?? value.length;
    setDraft(value);
    setTokenInfo(detectarToken(value, caret));
    requestAnimationFrame(syncMirrorScroll);
  };

  const insertarTag = (tag) => {
    if (!tokenInfo) return;

    const token = formatTagForInput(tag);
    const before = draft.slice(0, tokenInfo.start);
    const after = draft.slice(tokenInfo.end);
    const nextDraft = `${before}${token} ${after}`;
    setDraft(nextDraft);
    setTokenInfo(null);
    setSugerencias([]);
  };

  const agregarPendiente = async () => {
    const text = draft.trim();
    if (!text || busyId) return;

    const cat = destinoGuardado || CAT_COMUN;
    setBusyId('add');
    try {
      const { data } = await pendientesListaAPI.crear({ texto: text, categoria: cat });
      setPendientes((prev) => [...prev, data]);
      setDraft('');
      setTokenInfo(null);
      setSugerencias([]);
    } catch (error) {
      console.error('Error creando pendiente:', error);
      setErrorLista(
        error?.response?.data?.error || error?.message || 'No se pudo guardar el pendiente'
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleComposeKeyDown = (event) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    if (event.nativeEvent?.isComposing) return;

    if (tokenInfo && sugerencias.length > 0 && !loadingSugerencias) {
      event.preventDefault();
      insertarTag(sugerencias[0]);
      return;
    }

    event.preventDefault();
    const texto = draft.trim();
    if (!texto || busyId === 'add') return;

    void agregarPendiente();
  };

  const togglePendiente = async (id, completadoActual) => {
    if (busyId) return;
    const siguiente = !completadoActual;
    const flashAntesSalidaActivos = !completadoActual && siguiente;

    setBusyId(id);
    try {
      if (flashAntesSalidaActivos) {
        setSalidaAnimId(id);
        setCompletandoVisualId(id);
        await new Promise((r) => window.setTimeout(r, msAnimSalida()));
      }
      const { data } = await pendientesListaAPI.actualizar(id, { completado: siguiente });
      setPendientes((prev) => prev.map((item) => (item.id === id ? data : item)));
    } catch (error) {
      console.error('Error actualizando pendiente:', error);
      setErrorLista(
        error?.response?.data?.error || error?.message || 'No se pudo actualizar el pendiente'
      );
    } finally {
      setBusyId(null);
      setSalidaAnimId(null);
      setCompletandoVisualId(null);
    }
  };

  const eliminarPendiente = async (id) => {
    if (busyId) return;
    setBusyId(id);
    try {
      setSalidaAnimId(id);
      await new Promise((r) => window.setTimeout(r, msAnimSalida()));
      await pendientesListaAPI.eliminar(id);
      setPendientes((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error('Error eliminando pendiente:', error);
      setErrorLista(
        error?.response?.data?.error || error?.message || 'No se pudo eliminar el pendiente'
      );
    } finally {
      setBusyId(null);
      setSalidaAnimId(null);
    }
  };

  const aplicarReorden = async (cat, sourceId, targetId) => {
    if (busyId || sourceId === targetId) return;
    const ids = pendientesOrdenados
      .filter((p) => !p.completado && categoriaOf(p) === cat)
      .map((p) => p.id);
    const from = ids.indexOf(sourceId);
    const to = ids.indexOf(targetId);
    if (from === -1 || to === -1) return;
    const next = [...ids];
    const [removed] = next.splice(from, 1);
    next.splice(to, 0, removed);
    setBusyId('reorder');
    setDraggingId(null);
    try {
      const { data } = await pendientesListaAPI.reordenar(next);
      setPendientes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error reordenando pendientes:', error);
      setErrorLista(
        error?.response?.data?.error || error?.message || 'No se pudo guardar el orden'
      );
    } finally {
      setBusyId(null);
    }
  };

  const goToExpediente = (expedienteId) => {
    onClose();
    navigate(`/expedientes/${expedienteId}`);
  };

  if (!isOpen) return null;

  return (
    <div className="pendientes-overlay" onClick={onClose}>
      <div
        className={`pendientes-modal ${listaAmpliada ? 'pendientes-modal--lista-ampliada' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pendientes-header">
          <h4 className="mb-0">Lista de Pendientes</h4>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            Cerrar
          </button>
        </div>

        <div className="pendientes-body">
          {errorLista && (
            <div className="alert alert-warning py-2 mb-3" role="alert">
              {errorLista}
              <button
                type="button"
                className="btn btn-sm btn-outline-dark ms-2"
                onClick={() => {
                  setErrorLista(null);
                  cargarLista();
                }}
              >
                Reintentar
              </button>
            </div>
          )}

          {loadingLista && (
            <div className="pendientes-empty mb-3">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Cargando pendientes...
            </div>
          )}

          <div className="pendientes-compose-block">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
              <span className="pendientes-eyebrow pendientes-eyebrow--violet">
                Guardar en
              </span>
              <div
                className="pendientes-destino-toggle"
                role="group"
                aria-label="Dónde guardar la nueva tarea"
              >
                <button
                  type="button"
                  className={`pendientes-destino-toggle-btn ${destinoGuardado === CAT_COMUN ? 'active' : ''}`}
                  onClick={() => setDestinoGuardado(CAT_COMUN)}
                >
                  Diario
                </button>
                <button
                  type="button"
                  className={`pendientes-destino-toggle-btn ${destinoGuardado === CAT_GENERAL ? 'active' : ''}`}
                  onClick={() => setDestinoGuardado(CAT_GENERAL)}
                >
                  General
                </button>
              </div>
            </div>
            <div className="pendientes-compose-wrap pendientes-compose-wrap--sm">
              <div ref={mirrorRef} className="pendientes-compose-mirror" aria-hidden>
                {draft ? (
                  renderFragments(draft, { expClickable: false })
                ) : (
                  <span className="pendientes-compose-placeholder">
                    Escribe aquí… # para etiquetar expediente, usuario u obra
                  </span>
                )}
              </div>
              <textarea
                id="pendientes-compose"
                ref={composeRef}
                className="form-control pendientes-compose-ta"
                rows={2}
                autoFocus
                title="Enter guarda · Mayús+Enter nueva línea"
                placeholder=""
                aria-label="Texto del pendiente. Enter para guardar. Mayús+Enter para nueva línea. Usa # para etiquetar."
                value={draft}
                onChange={handleDraftChange}
                onKeyDown={handleComposeKeyDown}
                onScroll={syncMirrorScroll}
                onSelect={syncMirrorScroll}
                spellCheck={false}
                disabled={busyId === 'add'}
              />
            </div>

            {tokenInfo && (
              <div className="pendientes-sugerencias pendientes-sugerencias--compact">
                {loadingSugerencias && (
                  <div className="pendientes-empty py-2">Buscando…</div>
                )}
                {!loadingSugerencias && sugerencias.length === 0 && (
                  <div className="pendientes-empty py-2">Sin resultados</div>
                )}
                {!loadingSugerencias &&
                  sugerencias.map((item) => (
                    <button
                      key={`${item.kind}-${item.id}`}
                      type="button"
                      className="pendientes-sugerencia-item"
                      onClick={() => insertarTag(item)}
                    >
                      <span className="pendientes-sugerencia-titulo">
                        {item.kind === 'obra' ? item.label : `#${item.label}`}
                      </span>
                      <span className="pendientes-sugerencia-subtitulo">{item.sublabel}</span>
                    </button>
                  ))}
              </div>
            )}

            <div className="pendientes-compose-actions mt-2">
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={agregarPendiente}
                disabled={busyId === 'add' || !draft.trim()}
              >
                {busyId === 'add' ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>

          <hr className="pendientes-sep" />

          {!loadingLista && pendientes.length > 0 && (
            <div className="pendientes-toolbar-lista mb-2">
              <div className="flex-grow-1 min-w-0">
                <span className="pendientes-eyebrow pendientes-eyebrow--teal d-block mb-1">
                  Ver pendientes · una categoría cada vez · scroll más abajo
                </span>
                <div className="pendientes-vista-seg" role="tablist" aria-label="Lista a mostrar">
                  {SECCIONES_PENDIENTES.map(({ cat: catSeg, titulo: tituloSeg }) => {
                    const seleccionado = listaVista === catSeg;
                    return (
                      <button
                        key={catSeg}
                        type="button"
                        role="tab"
                        aria-selected={seleccionado}
                        className={`pendientes-vista-seg__btn ${seleccionado ? 'is-selected' : ''}`}
                        onClick={() => setListaVista(catSeg)}
                      >
                        <span>{tituloSeg}</span>
                        <span className="pendientes-vista-seg__count">{conteosPorCategoria[catSeg]}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <button
                type="button"
                className="btn pendientes-ampliar-btn ms-2 flex-shrink-0"
                onClick={() => setListaAmpliada((v) => !v)}
                aria-pressed={listaAmpliada}
              >
                {listaAmpliada ? (
                  <>
                    <i className="fas fa-compress-alt me-1" aria-hidden />
                    Compactar lista
                  </>
                ) : (
                  <>
                    <i className="fas fa-expand-alt me-1" aria-hidden />
                    Ampliar lista
                  </>
                )}
              </button>
            </div>
          )}

          {(() => {
            const totalVistaSinCargar = loadingLista
              ? 0
              : pendientesOrdenados.filter((p) => categoriaOf(p) === listaVista).length;
            const muestraAyudaListaLarga =
              !listaAmpliada && !loadingLista && totalVistaSinCargar > 14;
            const ayudaListaLargaJsx = muestraAyudaListaLarga ? (
              <p className="pendientes-tip-lista mb-2 small text-muted mb-0">
                Hay {totalVistaSinCargar} pendientes en esta vista. Pulsa{' '}
                <strong className="text-secondary">Ampliar lista</strong> para más alto útil sin alargar
                tanto el modal.
              </p>
            ) : null;

            const listaInterior = (
              <div className="pendientes-lista">
                {!loadingLista && pendientes.length === 0 && (
                  <div className="pendientes-empty">No hay pendientes todavía.</div>
                )}

                {!loadingLista &&
                  SECCIONES_PENDIENTES.filter(({ cat }) => cat === listaVista).map(
                    ({ cat: catSeccion, titulo }) => {
                const ordenadosCat = pendientesOrdenados.filter((p) => categoriaOf(p) === catSeccion);
                const activos = ordenadosCat.filter((p) => !p.completado);
                const hechas = ordenadosCat.filter((p) => p.completado);
                const hechasAbierto = !!mostrarHechasPorCat[catSeccion];
                const vacíaSección = activos.length === 0 && hechas.length === 0;

                return (
                  <section key={catSeccion} className="pendientes-seccion">
                    <h6 className="pendientes-seccion-titulo mb-2">{titulo}</h6>

                    {vacíaSección && (
                      <div className="pendientes-empty py-2 pendientes-empty--inline small text-muted">
                        Nada aquí por ahora.
                      </div>
                    )}

                    {!vacíaSección && activos.length > 0 && (
                      <div
                        className="pendientes-subtitulo pendientes-subtitulo--muted"
                        title="Arrastra la fila para ordenar dentro de esta sección"
                      >
                        En curso
                      </div>
                    )}

                    {activos.map((item) => {
                      const puedeArrastrar =
                        !loadingLista &&
                        busyId !== 'reorder' &&
                        busyId !== item.id &&
                        busyId !== 'add' &&
                        salidaAnimId !== item.id;
                      return (
                        <div
                          key={item.id}
                          className={`pendiente-item pendiente-item-compact pendiente-item-draggable ${
                            draggingId === item.id ? 'pendiente-item-dragging' : ''
                          } ${salidaAnimId === item.id ? 'pendiente-item--salida-verde' : ''}`}
                          draggable={puedeArrastrar}
                          onDragStart={(e) => {
                            if (!puedeArrastrar) {
                              e.preventDefault();
                              return;
                            }
                            e.dataTransfer.setData('text/plain', String(item.id));
                            e.dataTransfer.effectAllowed = 'move';
                            setDraggingId(item.id);
                          }}
                          onDragEnd={() => setDraggingId(null)}
                          onDragOver={(e) => {
                            if (!puedeArrastrar) return;
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            const raw = e.dataTransfer.getData('text/plain');
                            const sourceId = parseInt(raw, 10);
                            if (Number.isNaN(sourceId)) return;
                            const origen = pendientes.find((p) => p.id === sourceId);
                            if (!origen || categoriaOf(origen) !== catSeccion) return;
                            void aplicarReorden(catSeccion, sourceId, item.id);
                          }}
                        >
                          <span className="pendiente-drag-hint" title="Arrastra para ordenar en esta lista">
                            <i className="fas fa-grip-vertical" aria-hidden />
                          </span>
                          <label className="pendiente-check">
                            <input
                              type="checkbox"
                              checked={completandoVisualId === item.id || item.completado}
                              disabled={loadingLista || busyId === item.id || busyId === 'reorder'}
                              onChange={() => togglePendiente(item.id, item.completado)}
                            />
                          </label>
                          <div className="pendiente-contenido">
                            {renderTextWithTags(item.texto, goToExpediente)}
                          </div>
                          <button
                            type="button"
                            className="btn btn-link btn-sm pendiente-btn-remove p-1"
                            disabled={
                              loadingLista ||
                              busyId === item.id ||
                              busyId === 'reorder' ||
                              salidaAnimId === item.id
                            }
                            onClick={() => eliminarPendiente(item.id)}
                            title={busyId === item.id ? '...' : 'Eliminar'}
                            aria-label="Eliminar pendiente"
                          >
                            <i
                              className={`fas ${busyId === item.id ? 'fa-spinner fa-spin' : 'fa-trash-alt'}`}
                              aria-hidden
                            />
                          </button>
                        </div>
                      );
                    })}

                    {!loadingLista && hechas.length > 0 && (
                      <button
                        type="button"
                        className="pendientes-completados-toggle"
                        onClick={() =>
                          setMostrarHechasPorCat((prev) => ({
                            ...prev,
                            [catSeccion]: !prev[catSeccion]
                          }))
                        }
                        aria-expanded={hechasAbierto}
                      >
                        <span className="pendientes-subtitulo pendientes-subtitulo--muted mb-0">
                          Hechas · {titulo} ({hechas.length})
                        </span>
                        <i
                          className={`fas fa-chevron-${hechasAbierto ? 'up' : 'down'} ms-2 small`}
                          aria-hidden
                        />
                      </button>
                    )}

                    {hechasAbierto &&
                      hechas.map((item) => (
                        <div
                          key={item.id}
                          className={`pendiente-item pendiente-item-compact ${
                            item.completado ? 'completado' : ''
                          } ${salidaAnimId === item.id ? 'pendiente-item--salida-verde' : ''}`}
                        >
                          <span className="pendiente-drag-hint pendiente-drag-spacer" aria-hidden>
                            <i className="fas fa-grip-vertical" />
                          </span>
                          <label className="pendiente-check">
                            <input
                              type="checkbox"
                              checked={item.completado}
                              disabled={loadingLista || busyId === item.id || busyId === 'reorder'}
                              onChange={() => togglePendiente(item.id, item.completado)}
                            />
                          </label>
                          <div className="pendiente-contenido">
                            {renderTextWithTags(item.texto, goToExpediente)}
                          </div>
                          <button
                            type="button"
                            className="btn btn-link btn-sm pendiente-btn-remove p-1"
                            disabled={
                              loadingLista || busyId === item.id || busyId === 'reorder' || salidaAnimId === item.id
                            }
                            onClick={() => eliminarPendiente(item.id)}
                            title={busyId === item.id ? '...' : 'Eliminar'}
                            aria-label="Eliminar pendiente"
                          >
                            <i
                              className={`fas ${busyId === item.id ? 'fa-spinner fa-spin' : 'fa-trash-alt'}`}
                              aria-hidden
                            />
                          </button>
                        </div>
                      ))}
                  </section>
                );
                  })}
              </div>
            );

            return (
              <>
                {ayudaListaLargaJsx}
                <div
                  className={`pendientes-lista-scroll ${
                    listaAmpliada ? 'pendientes-lista-scroll--ampliada' : ''
                  }`}
                >
                  {listaInterior}
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default PendientesModal;
